import { Filter } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import Icons from "@/components/icons";
import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import {
  FiltersDrawer,
  type FilterSectionConfig,
} from "@/components/ui/v2/filters/FiltersDrawer";
import { ColumnSelectorDropdown } from "@/components/ui/v2/table/column-selector-dropdown";
import {
  TableActiveFilters,
  type ActiveFilter,
} from "@/components/ui/v2/table/table-active-filters";
import { useColumnVisibility } from "@/components/ui/v2/table/useColumnVisibility";
import Tabs from "@/components/ui/v2/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import { URL_PARAMS } from "@/constants/urlParams";
import { useCampaigns } from "@/features/campaigns/api/useCampaigns";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import { useIntegrations } from "@/features/integrations/hooks/useIntegrations";
import type {
  ReportingVacancyFilters,
  VacancyReportingRecord,
} from "@/features/integrations/modules/reporting/api/types";
import {
  useReportingData,
  useReportingIntegrations,
} from "@/features/integrations/modules/reporting/api/useReportingData";
import {
  COLUMN_VISIBILITY_STORAGE_KEY,
  COLUMNS_BY_LEVEL,
  CUSTOM_CELL_RENDERERS_CONFIG,
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  FILTER_KEYS_FOR_CHIPS,
  FILTER_LABEL_KEYS,
  FILTER_PRESERVE_PARAM_KEYS,
  FILTER_SECTIONS_CONFIG,
  getColumnSelectorGroups,
  getGeneralReportingUiComponents,
  METRICS_CONFIG,
  PAGE_CONFIG,
  REPORTING_MIN_DATE,
  SORTABLE_KEYS_BY_LEVEL,
  TOTAL_COST_SUBTITLE_KEY_BY_LEVEL,
  TABLE_TOOLBAR_CONFIG,
  TITLE_COLUMN_MAX_LENGTH,
  VACANCY_FILTER_URL_KEYS,
  DATE_RANGE_PICKER_CONFIG,
  type TabLevel,
} from "@/features/integrations/modules/reporting/pages/GeneralReporting/GeneralReportingConfig";
import { renderComponent } from "@/features/integrations/shared/renderer";
import type { RenderContext } from "@/features/integrations/shared/renderer/types";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import { toTitleCase, truncateWithEllipsis } from "@/lib/utils/stringUtils";
import {
  formatLocalDateOnly,
  parseLocalDateOnly,
} from "@/utils/dateUtils";
import { useValueFormatter } from "@/utils/useValueFormatter";

const parseDate = (s: string | null, fallback: Date) =>
  parseLocalDateOnly(s ?? undefined) ?? fallback;

/**
 * Parse the optional plural :level URL segment into the canonical singular `ReportingLevel`.
 * Unknown / missing segments (including the bare `/reporting` path) fall back to Vacancy.
 */
const parseReportingLevel = (segment: string | undefined): TabLevel => {
  const lower = segment?.toLowerCase();
  return lower === "campaigns" || lower === "integrations" ? lower : "vacancies";
};

/** Null, undefined, or whitespace — a custom cell renderer defers to the schema renderer for these. */
const isBlank = (value: unknown) => value == null || String(value).trim() === "";

const METRIC_ICON_CLASS = "h-4 w-4 lg:h-6 lg:w-6 text-primary";

function getMetricIcon(valueKey: (typeof METRICS_CONFIG)[number]["valueKey"]) {
  switch (valueKey) {
  case "totalCost":
    return <Icons.EuroIcon className={METRIC_ICON_CLASS} />;
  case "averageCtr":
    return <Icons.LinkIcon className={METRIC_ICON_CLASS} />;
  case "averageCpc":
    return <Icons.VisibilityIcon className={METRIC_ICON_CLASS} />;
  case "averageCpsa":
    return <Icons.ApplyIcon className={METRIC_ICON_CLASS} />;
  case "averageCpa":
    return <Icons.PersonIcon className={METRIC_ICON_CLASS} />;
  default:
    return <Icons.BarChartIcon className={METRIC_ICON_CLASS} />;
  }
}

function ClientGeneralReportingPage() {
  const { t, i18n } = useTranslation();
  const { format: formatValue, na } = useValueFormatter();
  const { tenantId } = useAuth();
  const { clientId, level: levelParam } = useParams();
  const location = useLocation();
  const level = parseReportingLevel(levelParam);
  const basePath = useMemo(
    () => location.pathname.replace(/\/(vacancies|campaigns|integrations)\/?$/i, ""),
    [location.pathname],
  );
  const searchForLevel = useCallback(
    (targetLevel: TabLevel) => {
      const params = new URLSearchParams(location.search);
      const currentSort = params.get(URL_PARAMS.ORDER_BY);
      if (currentSort && !SORTABLE_KEYS_BY_LEVEL[targetLevel].has(currentSort)) {
        params.delete(URL_PARAMS.ORDER_BY);
        params.delete(URL_PARAMS.DESCENDING);
      }
      const next = params.toString();
      return next ? `?${next}` : "";
    },
    [location.search],
  );
  const levelTabs = useMemo(
    () => [
      { id: "vacancies", label: t("reporting.levels.vacancies", "Vacancies"), href: `${basePath}${searchForLevel("vacancies")}`, content: <></> },
      { id: "integrations", label: t("reporting.levels.integrations", "Integrations"), href: `${basePath}/integrations${searchForLevel("integrations")}`, content: <></> },
      { id: "campaigns", label: t("reporting.levels.campaigns", "Campaigns"), href: `${basePath}/campaigns${searchForLevel("campaigns")}`, content: <></> },
    ],
    [basePath, searchForLevel, t],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler<VacancyReportingRecord>();

  const {
    hiddenKeys: hiddenColumns,
    toggle: toggleColumn,
    reset: resetColumns,
  } = useColumnVisibility(COLUMN_VISIBILITY_STORAGE_KEY);
  const visibleColumns = useMemo(
    () => COLUMNS_BY_LEVEL[level].filter((c) => !hiddenColumns.includes(c.key)),
    [level, hiddenColumns],
  );
  // Group definitions live in the config; rebuilt each render so they re-translate on language change.
  const columnGroups = getColumnSelectorGroups();

  const startDate = parseDate(
    searchParams.get("startDate"),
    DEFAULT_START_DATE,
  );
  const endDate = parseDate(searchParams.get("endDate"), DEFAULT_END_DATE);
  const selectedIntegrations = searchParams.getAll("integrations");
  const selectedCampaignIds = searchParams.getAll("campaignIds");
  const vacancyFilters = useMemo<ReportingVacancyFilters | undefined>(() => {
    const result: Partial<ReportingVacancyFilters> = {};
    let hasAny = false;
    for (const key of VACANCY_FILTER_URL_KEYS) {
      const values = searchParams.getAll(key);
      if (values.length > 0) {
        result[key] = values;
        hasAny = true;
      }
    }
    return hasAny ? result : undefined;
  }, [searchParams]);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { data: integrationsData } = useReportingIntegrations({
    tenantId: tenantId ?? undefined,
    clientId: clientId ?? undefined,
  });
  const availableIntegrations = useMemo(
    () => integrationsData?.integrations ?? [],
    [integrationsData?.integrations],
  );
  const { data: integrationsList } = useIntegrations({
    tenantId: tenantId ?? undefined,
    clientId: clientId ?? undefined,
  });
  const integrationDisplayNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of integrationsList ?? []) {
      const label =
        i.displayName && i18n.exists(i.displayName)
          ? t(i.displayName)
          : i.displayName || i.name;
      map.set(i.name.toLowerCase(), label);
    }
    return map;
  }, [integrationsList, t, i18n]);
  // Campaign list for the campaign filter dropdown — sourced from /api/v2/Campaigns rather than from
  // FilterCounts so the lazy attribution bypass stays intact for users who never engage with the
  // campaign filter. pageSize 500 is well above any realistic per-client campaign count.
  // Scoped by the selected reporting date range so the dropdown only lists campaigns that had any
  // CampaignIntegration active in the window — picking a campaign that wasn't active would
  // return zero rows and confuse the user.
  const { data: campaignsPage } = useCampaigns({
    clientId: clientId ?? undefined,
    page: 1,
    pageSize: 500,
    activeFrom: formatLocalDateOnly(startDate),
    activeTo: formatLocalDateOnly(endDate),
  });
  const campaignNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of campaignsPage?.data ?? []) {
      map.set(c.id, c.name);
    }
    return map;
  }, [campaignsPage?.data]);
  const campaignFilterOptions = useMemo(
    () => (campaignsPage?.data ?? []).map((c) => ({ label: c.name, value: c.id, count: undefined })),
    [campaignsPage?.data],
  );
  const campaignIdsParam = selectedCampaignIds.length ? selectedCampaignIds : undefined;
  const { data: reportingFilterOptions, isLoading: vacancyFiltersLoading } =
    useReportingData({
      endpoint: "filterCounts",
      level,
      params: {
        clientId: clientId ?? undefined,
        tenantId: tenantId ?? undefined,
        startDate,
        endDate,
        campaignIds: campaignIdsParam,
      },
    });

  const query = useReportingData({
    endpoint: "aggregated",
    level,
    params: {
      startDate,
      endDate,
      tenantId: tenantId ?? undefined,
      clientId: clientId ?? undefined,
      page,
      pageSize,
      sortColumn: orderBy,
      sortDirection: orderBy ? (descending ? "desc" : "asc") : null,
      searchValue: debouncedSearchValue,
      integrations: selectedIntegrations.length ? selectedIntegrations : undefined,
      vacancyFilters,
      campaignIds: campaignIdsParam,
    },
  });

  const metricsQuery = useReportingData({
    endpoint: "aggregatedMetrics",
    level,
    params: {
      startDate,
      endDate,
      tenantId: tenantId ?? undefined,
      clientId: clientId ?? undefined,
      searchValue: debouncedSearchValue,
      integrations: selectedIntegrations.length
        ? selectedIntegrations
        : undefined,
      vacancyFilters,
      campaignIds: campaignIdsParam,
    },
  });

  const setDateRange = useCallback(
    (from?: Date, to?: Date) => {
      const next = new URLSearchParams(searchParams);
      if (from) next.set("startDate", formatLocalDateOnly(from));
      else next.delete("startDate");
      if (to) next.set("endDate", formatLocalDateOnly(to));
      else next.delete("endDate");
      next.set("page", "1");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const onFilterChange = useCallback(
    (key: string, value: unknown) => {
      if (
        key !== TABLE_TOOLBAR_CONFIG.dateRangeFilterKey ||
        value == null ||
        typeof value !== "object"
      )
        return;
      const range = value as { startDate?: string; endDate?: string };
      const from = range.startDate
        ? parseDate(range.startDate, DEFAULT_START_DATE)
        : undefined;
      const to = range.endDate
        ? parseDate(range.endDate, DEFAULT_END_DATE)
        : undefined;
      setDateRange(from, to);
    },
    [setDateRange],
  );

  const startDateStr =
    searchParams.get("startDate") ?? formatLocalDateOnly(DEFAULT_START_DATE);
  const endDateStr =
    searchParams.get("endDate") ?? formatLocalDateOnly(DEFAULT_END_DATE);
  const filterValues = useMemo(
    () => ({
      [TABLE_TOOLBAR_CONFIG.dateRangeFilterKey]: {
        startDate: startDateStr,
        endDate: endDateStr,
      },
    }),
    [startDateStr, endDateStr],
  );

  const filterSections = useMemo((): FilterSectionConfig[] => {
    const sections: FilterSectionConfig[] = [];
    for (const entry of FILTER_SECTIONS_CONFIG) {
      if (entry.type === "sources") {
        sections.push({
          key: entry.key,
          title: entry.titleKey,
          type: "sources",
          sourceOptions: [...availableIntegrations],
        });
      } else if (entry.type === "campaigns") {
        // Campaign filter options come from the dedicated /api/v2/Campaigns endpoint rather than
        // from FilterCounts — keeps the lazy attribution bypass intact.
        if (campaignFilterOptions.length) {
          sections.push({ key: entry.key, title: entry.titleKey, options: campaignFilterOptions });
        }
      } else {
        const options =
          reportingFilterOptions?.[
            entry.key as keyof typeof reportingFilterOptions
          ];
        if (options?.length) {
          if (entry.key === "vacancyId") {
            sections.push({ key: entry.key, title: entry.titleKey, options: options.map(({ label, value }) => ({ label, value, count: undefined })) });
          } else if (entry.key === "city") {
            sections.push({ key: entry.key, title: entry.titleKey, options: options.map((opt) => ({ ...opt, label: toTitleCase(opt.label) || opt.label })) });
          } else {
            sections.push({ key: entry.key, title: entry.titleKey, options });
          }
        }
      }
    }
    return sections;
  }, [reportingFilterOptions, availableIntegrations, campaignFilterOptions]);

  const activeVacancyFilters = useMemo(() => {
    // Per-key display label for a chip's raw URL value. Integrations and campaigns are looked up
    // from their respective registries (slug → display name); city is title-cased; everything
    // else renders the raw URL value.
    const valueLabelFor = (key: string, value: string): string => {
      if (key === "integrations") {
        return integrationDisplayNameByKey.get(value.toLowerCase()) ?? value;
      }
      if (key === "campaignIds") {
        return campaignNameById.get(value) ?? value;
      }
      if (key === "city") {
        return toTitleCase(value) || value;
      }
      return value;
    };
    return FILTER_KEYS_FOR_CHIPS.flatMap((key) =>
      searchParams.getAll(key).map((value) => ({
        key,
        label: t(FILTER_LABEL_KEYS[key as keyof typeof FILTER_LABEL_KEYS] ?? `common.${key}`, key),
        value,
        valueLabel: valueLabelFor(key, value),
      }))
    );
  }, [searchParams, t, integrationDisplayNameByKey, campaignNameById]);
  const hasActiveVacancyFilters = activeVacancyFilters.length > 0;

  const handleRemoveVacancyFilter = useCallback(
    (filter: ActiveFilter) => {
      const next = new URLSearchParams(searchParams);
      const paramKey = filter.key;
      const current = next.getAll(paramKey);
      next.delete(paramKey);
      current
        .filter((v) => v !== String(filter.value))
        .forEach((v) => next.append(paramKey, v));
      next.set(URL_PARAMS.PAGE, "1");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );
  const handleClearVacancyFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    FILTER_KEYS_FOR_CHIPS.forEach((k) => next.delete(k));
    next.set(URL_PARAMS.PAGE, "1");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const toolbarActions = (
    <>
      <ColumnSelectorDropdown
        groups={columnGroups}
        hiddenKeys={hiddenColumns}
        onToggle={toggleColumn}
        onReset={resetColumns}
        triggerLabel={t("reporting.columns", "Columns")}
        showAllLabel={t("reporting.showAllColumns", "Show all")}
      />
      <Button
        variant="outline"
        size="sm"
        type="button"
        onClick={() => setIsFiltersOpen(true)}
      >
        <Filter className="h-4 w-4" />
        {t("common.filters", "Filters")}
      </Button>
    </>
  );

  const ui = useMemo(
    () => getGeneralReportingUiComponents(t, level, visibleColumns),
    [t, level, visibleColumns],
  );

  // The jobSourceUrl deep link is built server-side and arrives on each row; the Link column renders it.
  const tableData = useMemo(() => query.data?.data ?? [], [query.data?.data]);

  const context = useMemo<RenderContext>(
    () => ({
      integrationName: PAGE_CONFIG.integrationName,
      moduleName: PAGE_CONFIG.moduleName,
      tenantId: tenantId ?? undefined,
      clientId: clientId ?? undefined,
      executeAction: async () => {},
      tableData,
      isLoading: query.isLoading || query.isFetching,
      pagination: {
        page,
        pageSize,
        totalRecords: query.data?.totalRecords ?? 0,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
      },
      orderBy: orderBy ?? undefined,
      descending: descending ?? undefined,
      onSort: handleSort as RenderContext["onSort"],
      emptyMessage: t(
        PAGE_CONFIG.emptyMessageKey,
        "No reporting data available",
      ),
      searchValue: searchValue ?? "",
      onSearchChange: setSearchValue,
      filterValues,
      onFilterChange,
      dateRangePickerOptions: {
        minDate: REPORTING_MIN_DATE,
        maxDate: DEFAULT_END_DATE,
        ...DATE_RANGE_PICKER_CONFIG,
      },
      customCellRenderers: Object.fromEntries([
        ...CUSTOM_CELL_RENDERERS_CONFIG.map(({ columnKey, format }) => [
          columnKey,
          (value: unknown) => formatValue(value == null ? null : Number(value), format),
        ]),
        // These renderers transform the value only; returning null for an empty one defers to the
        // schema renderer, so the column's showNullAs ("N/A") and its muted styling still apply.
        ["city", (value: unknown) => (isBlank(value) ? null : toTitleCase(String(value)))],
        // Integration-level rows carry the raw integration slug ("werkzoeken", "indeed"); map to the
        // user-facing display name from the integrations registry. Falls back to the slug when no match.
        ["integrationName", (value: unknown) => {
          if (isBlank(value)) return null;
          const slug = String(value).trim();
          return integrationDisplayNameByKey.get(slug.toLowerCase()) ?? slug;
        }],
        [
          "vacancyTitle",
          (value: unknown) => {
            if (isBlank(value)) return null;
            const full = String(value).trim();
            const truncated = truncateWithEllipsis(full, TITLE_COLUMN_MAX_LENGTH);
            if (truncated === full) return full;
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{truncated}</span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-sm">
                  {full}
                </TooltipContent>
              </Tooltip>
            );
          },
        ],
      ]),
    }),
    [
      tenantId,
      clientId,
      formatValue,
      tableData,
      query.data,
      query.isLoading,
      query.isFetching,
      page,
      pageSize,
      setPage,
      setPageSize,
      orderBy,
      descending,
      handleSort,
      t,
      searchValue,
      setSearchValue,
      filterValues,
      onFilterChange,
      integrationDisplayNameByKey,
    ],
  );

  const getMetricValue = useCallback(
    (
      metric: (typeof METRICS_CONFIG)[number],
      metricsData: typeof metricsQuery.data,
    ) => {
      if (metricsData == null) return na;
      const raw = metricsData[metric.valueKey];
      if (metric.valueKey === "totalCost" && (raw == null || Number(raw) === 0)) return na;
      return formatValue(raw == null ? null : Number(raw), metric.format);
    },
    [na, formatValue, metricsQuery],
  );

  return (
    <ModernPageLayout
      title={t(PAGE_CONFIG.titleKey, "Reporting")}
      subtitle={t(PAGE_CONFIG.subtitleKey, "View combined reporting metrics.")}
    >
      <div className="space-y-4">
        <Tabs tabs={levelTabs} defaultTabId={level} />
        {ui.map((component, index) => (
          <div key={`${component.component}-${index}`} className="space-y-4">
            {component.component === "dataTable" && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                {METRICS_CONFIG.map((metric) => {
                  const count =
                    metric.countKey === "totalRecords"
                      ? (query.data?.totalRecords ?? 0)
                      : ((metricsQuery.data?.[
                          metric.countKey as keyof typeof metricsQuery.data
                      ] ?? 0) as number);
                  // totalCost counts rows (vacancies/campaigns/integrations) so its subtitle is
                  // level-dependent; the other cards count metric sums (impressions/clicks/...)
                  // which are the same regardless of grouping.
                  const subtitleKey = metric.valueKey === "totalCost"
                    ? TOTAL_COST_SUBTITLE_KEY_BY_LEVEL[level]
                    : metric.subtitleKey;
                  return (
                    <MetricCard
                      key={metric.valueKey}
                      icon={getMetricIcon(metric.valueKey)}
                      label={t(metric.titleKey)}
                      value={getMetricValue(metric, metricsQuery.data ?? undefined)}
                      subtitleCount={{ count, labelKey: subtitleKey }}
                      variant="gradient"
                      isLoading={metricsQuery.isLoading || metricsQuery.isFetching}
                    />
                  );
                })}
              </div>
            )}
            {renderComponent(
              component,
              component.component === "tableToolbar"
                ? { ...context, customToolbarActions: toolbarActions }
                : context,
            )}
            {component.component === "tableToolbar" &&
              hasActiveVacancyFilters && (
              <TableActiveFilters
                filters={activeVacancyFilters}
                onRemoveFilter={handleRemoveVacancyFilter}
                onClearFilters={handleClearVacancyFilters}
              />
            )}
          </div>
        ))}
      </div>
      <FiltersDrawer
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        searchParams={searchParams}
        filterKeys={[...FILTER_KEYS_FOR_CHIPS]}
        filterSections={filterSections}
        entityName={t(PAGE_CONFIG.vacanciesFoundEntityKey, "Vacancies")}
        filtersLoading={vacancyFiltersLoading}
        totalCount={query.data?.totalRecords}
        preserveParamKeys={[...FILTER_PRESERVE_PARAM_KEYS]}
      />
    </ModernPageLayout>
  );
}

export default ClientGeneralReportingPage;
